/**
 * CreateDevisModal
 * Modal for creating/editing devis (quotes)
 * Supports contact search, custom contact entry, and line items
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  useCreateDevis,
  useUpdateDevis,
  useAddDevisLine,
  useUpdateDevisLine,
  useRemoveDevisLine,
  useNextDevisNumber,
  useContactSearch,
  useDevis,
} from '../../hooks/useDevis';
import { useProducts } from '../../hooks/useProducts';
import type { Devis, CreateDevisData, ContactSearchResult, Product } from '../../types/facturation.types';
import { VAT_RATES_PERCENT } from '../../constants/vat.constants';
import { validateEmail } from '../../utils/validation';
import { ModalErrorBoundary } from '../ErrorBoundary';
import { toISODateString, addDays, formatDate } from '../../utils/dateFormat';
import '../../facturation.css';

interface CreateDevisModalProps {
  isOpen: boolean;
  onClose: () => void;
  devis?: Devis | null;
  onSuccess?: () => void;
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Language options
const languages = [
  { code: 'fr', label: 'Francais' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

// Validity period options (in days)
const validityOptions = [
  { days: 10, label: '10 jours' },
  { days: 15, label: '15 jours' },
  { days: 30, label: '30 jours' },
  { days: 60, label: '60 jours' },
  { days: 90, label: '90 jours' },
];

// Line item form
interface LineItem {
  id?: number;
  productId?: number;
  productCode?: string;
  description: string;
  quantity: number;
  unitPriceHtva: number;
  tvaRate: number;
}

export const CreateDevisModal: React.FC<CreateDevisModalProps> = ({
  isOpen,
  onClose,
  devis,
  onSuccess,
}) => {
  // Contact mode
  const [contactMode, setContactMode] = useState<'search' | 'custom'>('search');
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState<ContactSearchResult | null>(null);
  const [showContactResults, setShowContactResults] = useState(false);

  // Form data
  const [formData, setFormData] = useState<CreateDevisData>({
    contactName: '',
    contactAddress: '',
    contactVatNumber: '',
    contactEmail: '',
    contactPhone: '',
    isCustomContact: false,
    devisDate: toISODateString(new Date()),
    validityDate: '',
    language: 'fr',
    notes: '',
  });

  // Line items
  const [lines, setLines] = useState<LineItem[]>([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [newLine, setNewLine] = useState<LineItem>({
    description: '',
    quantity: 1,
    unitPriceHtva: 0,
    tvaRate: VAT_RATES_PERCENT.STANDARD,
  });

  // Step state for creating new devis
  const [step, setStep] = useState<'form' | 'saving' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');

  // Track unsaved changes for beforeunload warning
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Email validation state
  const [emailError, setEmailError] = useState<string | null>(null);

  // Hooks
  const { data: nextNumber } = useNextDevisNumber();
  const { data: contactResults } = useContactSearch(contactSearchTerm);
  const { data: products } = useProducts(true);
  const { data: fullDevis, refetch: refetchDevis } = useDevis(devis?.id || 0);

  const createMutation = useCreateDevis();
  const updateMutation = useUpdateDevis();
  const addLineMutation = useAddDevisLine();
  const updateLineMutation = useUpdateDevisLine();
  const removeLineMutation = useRemoveDevisLine();

  // Reset form when modal opens/closes or devis changes
  useEffect(() => {
    if (isOpen) {
      if (devis) {
        // Editing existing devis
        setFormData({
          contactId: devis.contact_id || undefined,
          contactName: devis.contact_name,
          contactAddress: devis.contact_address || '',
          contactVatNumber: devis.contact_vat_number || '',
          contactEmail: devis.contact_email || '',
          contactPhone: devis.contact_phone || '',
          isCustomContact: devis.is_custom_contact,
          devisDate: toISODateString(devis.devis_date) || toISODateString(new Date()),
          validityDate: toISODateString(devis.validity_date),
          language: devis.language || 'fr',
          notes: devis.notes || '',
        });
        setContactMode(devis.is_custom_contact ? 'custom' : 'search');
        if (!devis.is_custom_contact && devis.contact_id) {
          setSelectedContact({
            contact_id: devis.contact_id,
            name: devis.contact_name,
            address: devis.contact_address,
            vat_number: devis.contact_vat_number,
            email: devis.contact_email,
            phone: devis.contact_phone,
          });
        }
      } else {
        // New devis
        const today = new Date();
        setFormData({
          contactName: '',
          contactAddress: '',
          contactVatNumber: '',
          contactEmail: '',
          contactPhone: '',
          isCustomContact: false,
          devisDate: toISODateString(today),
          validityDate: addDays(today, 10), // Default 10 days validity
          language: 'fr',
          notes: '',
        });
        setContactMode('search');
        setSelectedContact(null);
        setLines([]);
      }
      setStep('form');
      setErrorMessage('');
      setEmailError(null);
      setContactSearchTerm('');
      setShowContactResults(false);
      setHasUnsavedChanges(false);
      // Reset product selection state
      setShowProductDropdown(false);
      setNewLine({
        description: '',
        quantity: 1,
        unitPriceHtva: 0,
        tvaRate: VAT_RATES_PERCENT.STANDARD,
      });
    }
  }, [isOpen, devis]);

  // Load lines when editing existing devis
  useEffect(() => {
    if (fullDevis?.lines && isOpen && devis) {
      setLines(fullDevis.lines.map(l => ({
        id: l.id,
        productId: l.product_id || undefined,
        productCode: l.product_code || undefined,
        description: l.description,
        quantity: l.quantity,
        unitPriceHtva: l.unit_price_htva,
        tvaRate: l.tva_rate,
      })));
    }
  }, [fullDevis, isOpen, devis]);

  // Beforeunload warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && isOpen) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isOpen]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalHtva = 0;
    let totalTva = 0;

    lines.forEach(line => {
      const lineHtva = line.quantity * line.unitPriceHtva;
      const lineTva = lineHtva * (line.tvaRate / 100);
      totalHtva += lineHtva;
      totalTva += lineTva;
    });

    return {
      totalHtva,
      totalTva,
      totalTvac: totalHtva + totalTva,
    };
  }, [lines]);

  // Handle contact selection
  const handleSelectContact = (contact: ContactSearchResult) => {
    setSelectedContact(contact);
    setFormData({
      ...formData,
      contactId: contact.contact_id,
      contactName: contact.name,
      contactAddress: contact.address || '',
      contactVatNumber: contact.vat_number || '',
      contactEmail: contact.email || '',
      contactPhone: contact.phone || '',
      isCustomContact: false,
    });
    setShowContactResults(false);
    setContactSearchTerm(contact.name);
    setHasUnsavedChanges(true);
  };

  // Handle product selection for new line
  const handleSelectProduct = (product: Product) => {
    setNewLine({
      ...newLine,
      productId: product.id,
      productCode: product.product_code,
      description: product.description,
      unitPriceHtva: product.unit_price_htva,
      tvaRate: product.tva_rate,
    });
  };

  // Add line to devis
  const handleAddLine = async () => {
    if (!newLine.description || newLine.unitPriceHtva <= 0) {
      alert('Veuillez remplir la description et le prix');
      return;
    }

    if (devis) {
      // Add line to existing devis via API
      try {
        await addLineMutation.mutateAsync({
          devisId: devis.id,
          data: {
            productId: newLine.productId,
            productCode: newLine.productCode,
            description: newLine.description,
            quantity: newLine.quantity,
            unitPriceHtva: newLine.unitPriceHtva,
            tvaRate: newLine.tvaRate,
          },
        });
        refetchDevis();
      } catch (error) {
        console.error('Add line failed:', error);
        alert('Erreur lors de l\'ajout de la ligne');
        return;
      }
    } else {
      // Add to local state for new devis
      setLines([...lines, { ...newLine }]);
    }

    // Reset new line form
    setNewLine({
      description: '',
      quantity: 1,
      unitPriceHtva: 0,
      tvaRate: VAT_RATES_PERCENT.STANDARD,
    });
    setShowProductDropdown(false);
    setHasUnsavedChanges(true);
  };

  // Remove line
  const handleRemoveLine = async (index: number) => {
    const line = lines[index];

    if (devis && line.id) {
      // Remove from existing devis via API
      try {
        await removeLineMutation.mutateAsync({
          devisId: devis.id,
          lineId: line.id,
        });
        refetchDevis();
      } catch (error) {
        console.error('Remove line failed:', error);
        alert('Erreur lors de la suppression de la ligne');
        return;
      }
    } else {
      // Remove from local state
      setLines(lines.filter((_, i) => i !== index));
    }
    setHasUnsavedChanges(true);
  };

  // Save devis
  const handleSave = async () => {
    // Validate
    if (!formData.contactName) {
      alert('Veuillez specifier un contact');
      return;
    }

    // Validate email if provided
    if (formData.contactEmail && formData.contactEmail.trim().length > 0) {
      const emailValidation = validateEmail(formData.contactEmail, false);
      if (!emailValidation.valid) {
        alert(emailValidation.error || 'Format d\'email invalide');
        return;
      }
    }

    if (lines.length === 0) {
      alert('Veuillez ajouter au moins une ligne');
      return;
    }

    setStep('saving');

    try {
      if (devis) {
        // Update existing devis
        await updateMutation.mutateAsync({
          id: devis.id,
          data: formData,
        });
      } else {
        // Create new devis with lines
        await createMutation.mutateAsync({
          ...formData,
          lines: lines.map(l => ({
            productId: l.productId,
            productCode: l.productCode,
            description: l.description,
            quantity: l.quantity,
            unitPriceHtva: l.unitPriceHtva,
            tvaRate: l.tvaRate,
          })),
        });
      }
      setStep('success');
      setHasUnsavedChanges(false);
      onSuccess?.();
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  return (
    <ModalErrorBoundary>
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        onClose={onClose}
        className="relative z-50"
        // Focus trap is automatically enabled by HeadlessUI Dialog
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header - Focus trap is automatically handled by HeadlessUI Dialog */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        {devis ? `Modifier devis ${devis.devis_number}` : 'Nouveau devis'}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {devis ? 'Modifier les details du devis' : `Prochain numero: ${nextNumber || '...'}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="facturation-btn-modal-close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  {step === 'form' && (
                    <div className="space-y-6">
                      {/* Contact Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-700">Contact</h4>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setContactMode('search');
                                setFormData({ ...formData, isCustomContact: false });
                                setHasUnsavedChanges(true);
                              }}
                              className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                contactMode === 'search'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                            >
                              Rechercher
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setContactMode('custom');
                                setSelectedContact(null);
                                setFormData({
                                  ...formData,
                                  contactId: undefined,
                                  isCustomContact: true,
                                });
                                setHasUnsavedChanges(true);
                              }}
                              className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                contactMode === 'custom'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                            >
                              Personnalise
                            </button>
                          </div>
                        </div>

                        {contactMode === 'search' ? (
                          <div className="relative">
                            {selectedContact ? (
                              /* Selected contact card with X to remove */
                              <div className="p-3 bg-white rounded-lg border border-blue-200">
                                <div className="flex items-start gap-3">
                                  <UserIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{selectedContact.name}</div>
                                    {selectedContact.address && (
                                      <div className="text-sm text-gray-500">{selectedContact.address}</div>
                                    )}
                                    {selectedContact.vat_number && (
                                      <div className="text-sm text-gray-500">TVA: {selectedContact.vat_number}</div>
                                    )}
                                    {selectedContact.email && (
                                      <div className="text-sm text-gray-500">{selectedContact.email}</div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedContact(null);
                                      setContactSearchTerm('');
                                      setFormData({
                                        ...formData,
                                        contactId: undefined,
                                        contactName: '',
                                        contactAddress: '',
                                        contactVatNumber: '',
                                        contactEmail: '',
                                        contactPhone: '',
                                      });
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Supprimer le contact"
                                  >
                                    <XMarkIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Search input when no contact selected */
                              <>
                                <div className="relative">
                                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                  <input
                                    type="text"
                                    value={contactSearchTerm}
                                    onChange={(e) => {
                                      setContactSearchTerm(e.target.value);
                                      setShowContactResults(true);
                                    }}
                                    onFocus={() => setShowContactResults(true)}
                                    placeholder="Rechercher un contact..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    aria-label="Rechercher un contact par nom ou numéro TVA"
                                  />
                                </div>
                                {showContactResults && contactResults && contactResults.length > 0 && (
                                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {contactResults.map((contact) => (
                                      <button
                                        key={contact.contact_id}
                                        type="button"
                                        onClick={() => handleSelectContact(contact)}
                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                                      >
                                        <div className="font-medium text-gray-900">{contact.name}</div>
                                        {contact.vat_number && (
                                          <div className="text-sm text-gray-500">{contact.vat_number}</div>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                              <input
                                type="text"
                                value={formData.contactName}
                                onChange={(e) => {
                                  setFormData({ ...formData, contactName: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Numero TVA</label>
                              <input
                                type="text"
                                value={formData.contactVatNumber}
                                onChange={(e) => {
                                  setFormData({ ...formData, contactVatNumber: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="BE0123456789"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
                              <textarea
                                value={formData.contactAddress}
                                onChange={(e) => {
                                  setFormData({ ...formData, contactAddress: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                rows={2}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                              <input
                                type="email"
                                value={formData.contactEmail}
                                onChange={(e) => {
                                  setFormData({ ...formData, contactEmail: e.target.value });
                                  setEmailError(null); // Clear error on change
                                  setHasUnsavedChanges(true);
                                }}
                                onBlur={(e) => {
                                  // Validate on blur if field is not empty
                                  const email = e.target.value.trim();
                                  if (email.length > 0) {
                                    const validation = validateEmail(email, false);
                                    setEmailError(validation.error);
                                  } else {
                                    setEmailError(null);
                                  }
                                }}
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  emailError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                                placeholder="exemple@domaine.com"
                              />
                              {emailError && (
                                <p id="email-error" className="mt-1 text-xs text-red-600" role="alert">{emailError}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Telephone</label>
                              <input
                                type="tel"
                                value={formData.contactPhone}
                                onChange={(e) => {
                                  setFormData({ ...formData, contactPhone: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Date Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Date du devis</label>
                          <div className="relative">
                            <input
                              type="date"
                              value={formData.devisDate}
                              onChange={(e) => {
                                setFormData({ ...formData, devisDate: e.target.value });
                                setHasUnsavedChanges(true);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            {formData.devisDate === toISODateString(new Date()) && (
                              <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-xs text-blue-600 font-medium">
                                Aujourd'hui
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Validite</label>
                          <div className="flex items-center gap-2">
                            <select
                              value=""
                              onChange={(e) => {
                                const days = parseInt(e.target.value);
                                if (days) {
                                  const baseDate = formData.devisDate ? new Date(formData.devisDate) : new Date();
                                  setFormData({ ...formData, validityDate: addDays(baseDate, days) });
                                  setHasUnsavedChanges(true);
                                }
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="">
                                {formData.validityDate
                                  ? formatDate(formData.validityDate)
                                  : '-- Choisir --'}
                              </option>
                              {validityOptions.map((opt) => (
                                <option key={opt.days} value={opt.days}>
                                  + {opt.label}
                                </option>
                              ))}
                            </select>
                            {formData.validityDate && formData.devisDate && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                ({Math.round((new Date(formData.validityDate).getTime() - new Date(formData.devisDate).getTime()) / (1000 * 60 * 60 * 24))} jours)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Lines Section - Redesigned */}
                      <div className="space-y-4">
                        {/* Add line - searchable product or custom */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex gap-3">
                            {/* Product search / Description */}
                            <div className="flex-1 relative">
                              <label className="block text-xs font-medium text-gray-500 mb-1">Produit / Description</label>
                              <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={newLine.description}
                                  onChange={(e) => {
                                    setNewLine({ ...newLine, description: e.target.value, productId: undefined, productCode: undefined });
                                    setShowProductDropdown(true);
                                  }}
                                  onFocus={() => setShowProductDropdown(true)}
                                  onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                                  placeholder="Rechercher ou saisir une description..."
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                                  aria-label="Rechercher un produit ou saisir une description"
                                />
                              </div>
                              {/* Product suggestions dropdown */}
                              {showProductDropdown && products && products.length > 0 && (
                                (() => {
                                  const searchTerm = newLine.description?.toLowerCase() || '';
                                  const filtered = searchTerm.length > 0
                                    ? products.filter(p =>
                                        p.description.toLowerCase().includes(searchTerm) ||
                                        p.product_code.toLowerCase().includes(searchTerm)
                                      )
                                    : products;
                                  const displayProducts = filtered.slice(0, 10);
                                  if (displayProducts.length === 0) return null;
                                  return (
                                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                      {displayProducts.map((product) => (
                                        <button
                                          key={product.id}
                                          type="button"
                                          onClick={() => {
                                            setNewLine({
                                              ...newLine,
                                              productId: product.id,
                                              productCode: product.product_code,
                                              description: product.description,
                                              unitPriceHtva: product.unit_price_htva,
                                              tvaRate: product.tva_rate,
                                            });
                                            setShowProductDropdown(false);
                                          }}
                                          className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center justify-between gap-2 border-b last:border-b-0"
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-900 truncate">{product.description}</div>
                                            <div className="text-xs text-gray-400">{product.product_code}</div>
                                          </div>
                                          <div className="text-sm font-medium text-blue-600 whitespace-nowrap">
                                            {formatCurrency(product.unit_price_htva)}
                                          </div>
                                        </button>
                                      ))}
                                      {filtered.length > 10 && (
                                        <div className="px-3 py-2 text-xs text-gray-400 text-center bg-gray-50">
                                          +{filtered.length - 10} autres produits...
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()
                              )}
                            </div>

                            {/* Quantity */}
                            <div className="w-20">
                              <label className="block text-xs font-medium text-gray-500 mb-1">Qte</label>
                              <input
                                type="number"
                                value={newLine.quantity}
                                onChange={(e) => setNewLine({ ...newLine, quantity: parseFloat(e.target.value) || 1 })}
                                min="0.01"
                                step="1"
                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-blue-500 focus:border-blue-500"
                                aria-label="Quantité"
                              />
                            </div>

                            {/* Price */}
                            <div className="w-28">
                              <label className="block text-xs font-medium text-gray-500 mb-1">Prix HTVA</label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={newLine.unitPriceHtva || ''}
                                  onChange={(e) => setNewLine({ ...newLine, unitPriceHtva: parseFloat(e.target.value) || 0 })}
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  className="w-full px-2 py-2 pr-6 border border-gray-300 rounded-lg text-sm text-right focus:ring-blue-500 focus:border-blue-500"
                                  aria-label="Prix unitaire HTVA"
                                  aria-required="true"
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                              </div>
                            </div>

                            {/* TVA */}
                            <div className="w-20">
                              <label className="block text-xs font-medium text-gray-500 mb-1">TVA</label>
                              <select
                                value={newLine.tvaRate}
                                onChange={(e) => setNewLine({ ...newLine, tvaRate: parseFloat(e.target.value) })}
                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value={VAT_RATES_PERCENT.STANDARD}>{VAT_RATES_PERCENT.STANDARD}%</option>
                                <option value={VAT_RATES_PERCENT.REDUCED}>{VAT_RATES_PERCENT.REDUCED}%</option>
                                <option value={VAT_RATES_PERCENT.SUPER_REDUCED}>{VAT_RATES_PERCENT.SUPER_REDUCED}%</option>
                                <option value={VAT_RATES_PERCENT.ZERO}>{VAT_RATES_PERCENT.ZERO}%</option>
                              </select>
                            </div>

                            {/* Add button */}
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={handleAddLine}
                                disabled={!newLine.description || newLine.unitPriceHtva <= 0}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                              >
                                <PlusIcon className="h-4 w-4" />
                                <span className="text-sm">Ajouter</span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Lines list */}
                        {lines.length > 0 && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr className="text-xs text-gray-500 uppercase">
                                  <th className="px-4 py-2 text-left font-medium">Description</th>
                                  <th className="px-3 py-2 text-center font-medium w-20">Qte</th>
                                  <th className="px-3 py-2 text-right font-medium w-24">Prix HTVA</th>
                                  <th className="px-3 py-2 text-center font-medium w-16">TVA</th>
                                  <th className="px-3 py-2 text-right font-medium w-28">Total TVAC</th>
                                  <th className="px-2 py-2 w-10"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {lines.map((line, index) => (
                                  <tr key={line.id || index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                      <span className="text-sm text-gray-900">{line.description}</span>
                                      {line.productCode && (
                                        <span className="ml-2 text-xs text-gray-400">[{line.productCode}]</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 text-center text-sm text-gray-600">{line.quantity}</td>
                                    <td className="px-3 py-3 text-right text-sm text-gray-600">{formatCurrency(line.unitPriceHtva)}</td>
                                    <td className="px-3 py-3 text-center text-sm text-gray-500">{line.tvaRate}%</td>
                                    <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">
                                      {formatCurrency(line.quantity * line.unitPriceHtva * (1 + line.tvaRate / 100))}
                                    </td>
                                    <td className="px-2 py-3">
                                      {(!devis || devis.status === 'DRAFT') && (
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveLine(index)}
                                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {lines.length === 0 && (
                          <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                            Cliquez sur un produit ci-dessus ou ajoutez une ligne personnalisee
                          </div>
                        )}

                        {/* Totals */}
                        {lines.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total HTVA:</span>
                                <span>{formatCurrency(totals.totalHtva)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">TVA:</span>
                                <span>{formatCurrency(totals.totalTva)}</span>
                              </div>
                              <div className="flex justify-between font-semibold text-base pt-2 border-t">
                                <span>Total TVAC:</span>
                                <span className="text-blue-600">{formatCurrency(totals.totalTvac)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => {
                            setFormData({ ...formData, notes: e.target.value });
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          rows={3}
                          placeholder="Notes ou remarques..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Saving state */}
                  {step === 'saving' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Enregistrement en cours...</p>
                    </div>
                  )}

                  {/* Success state */}
                  {step === 'success' && (
                    <div className="text-center py-8">
                      <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">
                        {devis ? 'Devis modifie' : 'Devis cree'}
                      </h3>
                      <p className="mt-2 text-gray-600">
                        Le devis a ete {devis ? 'modifie' : 'cree'} avec succes.
                      </p>
                    </div>
                  )}

                  {/* Error state */}
                  {step === 'error' && (
                    <div className="text-center py-8">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                      <p className="mt-2 text-red-600">{errorMessage}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  {/* Language selection badges */}
                  {step === 'form' && (
                    <div className="flex items-center gap-1">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, language: lang.code });
                            setHasUnsavedChanges(true);
                          }}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            formData.language === lang.code
                              ? 'bg-[#ef5343] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          aria-label={`Langue: ${lang.label}`}
                          aria-pressed={formData.language === lang.code}
                        >
                          {lang.code.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  )}
                  {(step === 'success' || step === 'error') && <div />}

                  {/* Action buttons */}
                  <div className="flex items-center gap-3">
                    {step === 'form' && (
                      <>
                        <button
                          onClick={onClose}
                          className="facturation-btn-modal-cancel"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={!formData.contactName || lines.length === 0}
                          className="facturation-btn-modal-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: '#3b82f6' }}
                        >
                          {devis ? 'Enregistrer' : 'Creer le devis'}
                        </button>
                      </>
                    )}
                    {(step === 'success' || step === 'error') && (
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-primary"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        Fermer
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
    </ModalErrorBoundary>
  );
};

export default CreateDevisModal;
